import config from '../../config';

/**
 * Push Notification Manager
 * Handles subscription, unsubscription, and permission requests for push notifications
 */
class PushNotificationManager {
  constructor() {
    this.registration = null;
    this.subscription = null;
  }

  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission; // 'default', 'granted', or 'denied'
  }

  /**
   * Initialize push notifications - check status and subscribe if already granted
   * Does NOT request permission - use requestPermission() for that
   */
  async initialize() {
    if (!this.isSupported()) {
      console.warn('Push notifications are not supported in this browser');
      return { supported: false, error: 'Push notifications not supported' };
    }

    try {
      // Check current permission status
      const permission = this.getPermissionStatus();
      
      if (permission === 'denied') {
        console.warn('Notification permission was previously denied');
        return { 
          supported: true, 
          subscribed: false, 
          permission: 'denied',
          error: 'Permission denied. Please enable notifications in your browser settings.' 
        };
      }

      if (permission !== 'granted') {
        // Permission not granted yet - don't request automatically
        return { 
          supported: true, 
          subscribed: false, 
          permission: 'default',
          message: 'Notification permission not granted yet' 
        };
      }

      // Permission is granted - proceed with subscription
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      this.registration = registration;

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        this.subscription = existingSubscription;
        // Verify subscription is still valid on server
        await this.verifySubscription(existingSubscription);
        return { supported: true, subscribed: true, subscription: existingSubscription };
      }

      // Subscribe to push notifications
      return await this.subscribe();
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      return { supported: true, subscribed: false, error: error.message };
    }
  }

  /**
   * Request notification permission from user
   * Should be called in response to user interaction (e.g., button click)
   */
  async requestPermission() {
    if (!this.isSupported()) {
      return { supported: false, error: 'Push notifications not supported' };
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        return { 
          supported: true, 
          subscribed: false, 
          permission,
          error: permission === 'denied' 
            ? 'Permission denied' 
            : 'Permission not granted' 
        };
      }

      // Permission granted - now subscribe
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      this.registration = registration;

      // Subscribe to push notifications
      return await this.subscribe();
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return { supported: true, subscribed: false, error: error.message };
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    if (!this.registration) {
      const registration = await navigator.serviceWorker.ready;
      this.registration = registration;
    }

    try {
      // Get VAPID public key from server
      const vapidKeyResponse = await fetch(`${config.apiUrl}/getVapidPublicKey`);
      const vapidKeyData = await vapidKeyResponse.json();
      
      if (!vapidKeyData.publicKey) {
        throw new Error('VAPID public key not available');
      }

      // Convert VAPID key to Uint8Array
      const vapidPublicKey = this.urlBase64ToUint8Array(vapidKeyData.publicKey);

      // Subscribe to push notifications
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      });

      this.subscription = subscription;

      // Send subscription to server
      await this.sendSubscriptionToServer(subscription);

      return { supported: true, subscribed: true, subscription };
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return { supported: true, subscribed: false, error: error.message };
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    if (!this.subscription) {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      if (!existingSubscription) {
        return { success: true, message: 'Not subscribed' };
      }
      this.subscription = existingSubscription;
    }

    try {
      // Unsubscribe from push service
      await this.subscription.unsubscribe();
      
      // Remove subscription from server
      await this.removeSubscriptionFromServer(this.subscription);

      this.subscription = null;
      return { success: true, message: 'Unsubscribed successfully' };
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user is subscribed
   */
  async isSubscribed() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Send subscription to server
   */
  async sendSubscriptionToServer(subscription) {
    const user = localStorage.getItem('user');
    if (!user) {
      throw new Error('User not logged in');
    }

    const userObj = JSON.parse(user);
    const subscriptionData = {
      userId: userObj.id,
      subscription: subscription.toJSON()
    };

    const response = await fetch(`${config.apiUrl}/savePushSubscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(subscriptionData)
    });

    if (!response.ok) {
      throw new Error('Failed to save subscription to server');
    }

    const result = await response.json();
    console.log('Subscription saved to server:', result);
    return result;
  }

  /**
   * Remove subscription from server
   */
  async removeSubscriptionFromServer(subscription) {
    const user = localStorage.getItem('user');
    if (!user) {
      return;
    }

    const userObj = JSON.parse(user);
    const subscriptionData = {
      userId: userObj.id,
      endpoint: subscription.endpoint
    };

    try {
      const response = await fetch(`${config.apiUrl}/removePushSubscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(subscriptionData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Subscription removed from server:', result);
      }
    } catch (error) {
      console.error('Error removing subscription from server:', error);
    }
  }

  /**
   * Verify subscription is still valid on server
   */
  async verifySubscription(subscription) {
    try {
      await this.sendSubscriptionToServer(subscription);
    } catch (error) {
      console.error('Error verifying subscription:', error);
    }
  }

  /**
   * Convert VAPID key from base64 URL to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Export singleton instance
export default new PushNotificationManager();

